import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBotsFleet } from '@/api/hooks/use-bots';
import { useVenueConnections } from '@/api/hooks/use-venue-connections';

export interface Crumb {
  /** Render label (already entity-resolved when applicable). */
  label: string;
  /** Untruncated label for the `title` tooltip. */
  fullLabel: string;
  /** href for non-current crumbs; undefined for the terminal one. */
  href?: string;
  isCurrent: boolean;
}

const STATIC_LABELS: Record<string, string> = {
  dashboard: 'nav.dashboard',
  bots: 'nav.bots',
  create: 'buttons.create',
  'execution-profiles': 'nav.executionProfiles',
  'venue-connections': 'nav.venueConnections',
  strategies: 'nav.strategies',
  configs: 'nav.strategies',
  wallets: 'nav.wallets',
  portfolio: 'nav.portfolio',
  orders: 'nav.orders',
  positions: 'nav.positions',
  signals: 'nav.signals',
  risk: 'nav.riskSafety',
  backtests: 'nav.backtests',
  compare: 'buttons.preview',
  'market-data': 'nav.marketData',
  settings: 'nav.settings',
  glossary: 'nav.glossary',
  system: 'nav.system',
  admin: 'nav.admin',
  audit: 'nav.audit',
  'security-events': 'nav.securityEvents',
  'feature-flags': 'nav.featureFlags',
  dlq: 'nav.dlq',
};

const ID_PATTERN = /^([0-9]+|[a-z]_?[A-Za-z0-9]{6,}|[0-9a-f-]{8,})$/;

function looksLikeId(segment: string): boolean {
  return ID_PATTERN.test(segment);
}

function truncateId(id: string, max = 12): string {
  if (id.length <= max) return id;
  return id.slice(0, max - 1) + '…';
}

export function useBreadcrumbs(): Crumb[] {
  const location = useLocation();
  const { t } = useTranslation('common');

  // Pull cached lists for entity name resolution. These hooks are already
  // wired with TanStack Query polling — no extra requests are issued here.
  const { data: botsList } = useBotsFleet();
  const { data: connectionsList } = useVenueConnections({ limit: 200 });

  return useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return [];

    const crumbs: Crumb[] = [];
    let parentSegment: string | undefined;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      if (!segment) continue;
      const href = '/' + segments.slice(0, i + 1).join('/');
      const isCurrent = i === segments.length - 1;

      const staticKey = STATIC_LABELS[segment];
      let fullLabel: string;
      if (staticKey) {
        fullLabel = t(staticKey);
      } else if (looksLikeId(segment)) {
        fullLabel = resolveEntityName(parentSegment, segment, {
          bots: botsList?.items,
          connections: connectionsList?.items,
        });
      } else {
        // Unknown segment — humanize the slug as a sensible fallback.
        fullLabel = segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      }

      crumbs.push({
        label: fullLabel,
        fullLabel,
        ...(isCurrent ? {} : { href }),
        isCurrent,
      });

      parentSegment = segment;
    }

    return crumbs;
  }, [location.pathname, botsList, connectionsList, t]);
}

interface EntitySources {
  bots?: ReadonlyArray<{ id: string | number; name?: string | null }>;
  connections?: ReadonlyArray<{
    id: string;
    label?: string | null;
    venue?: string | null;
  }>;
}

function resolveEntityName(parent: string | undefined, id: string, sources: EntitySources): string {
  if (parent === 'bots' && sources.bots) {
    const match = sources.bots.find((b) => String(b.id) === id);
    if (match?.name) return match.name;
  }
  if (parent === 'venue-connections' && sources.connections) {
    const match = sources.connections.find((c) => c.id === id);
    if (match) return match.label ?? match.venue ?? truncateId(id);
  }
  // No resolver wired for this parent (or list still loading) — fall back to
  // truncated ID per spec § Decision 4.
  return truncateId(id);
}
